// ===============================================
// File: src/contexts/MarketingContext.jsx
// Unified persistence (Folder OR Drive) via StorageContext.
// Rehydrates on boot and after restore events.
// Emits subtle toasts on load/save errors (unchanged).
// ===============================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useStorage } from "./StorageContext";
import { useNotifications } from "./NotificationsContext";

const FILE_NAME = "marketing/mudoMarketing.json";

const MarketingContext = createContext({
  campaigns: [],
  monthlyTotal: 0,
  addCampaign: () => {},
  updateCampaign: () => {},
  removeCampaign: () => {},
  clearAll: () => {},
});

function toast(message, type = "info") {
  try {
    window.dispatchEvent(
      new CustomEvent("mudo:toast", { detail: { type, message } })
    );
  } catch {}
}

// ----- safe parse from disk (array or {campaigns}) -----
function coerceCampaignList(v) {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.campaigns)) return v.campaigns;
  return [];
}

// ----- normalization -----
function normalizeCampaign(c) {
  return {
    id: c?.id || (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now())),
    platform: String(c?.platform || "Unnamed").trim(),
    cost: Number(c?.cost || 0),
  };
}

export function MarketingProvider({ children }) {
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = useStorage();
  const { notifyAndInbox } = useNotifications() || {};

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  const [campaigns, setCampaigns] = useState([]);
  const loadedRef = useRef(false);
  const userMutatedRef = useRef(false);
  const debounceRef = useRef(0);

  const notifyMarketing = useCallback(
    ({ id, message, level = "info" }) => {
      toast(message, level);
      notifyAndInbox?.({
        id,
        createdAt: Date.now(),
        level,
        title: "Marketing",
        message,
        source: "system",
        world: "studio",
        entity: { type: "marketing" },
        action: { kind: "NAVIGATE", to: "/dashboard/home" },
      });
    },
    [notifyAndInbox]
  );

  // ----- hydrate when workspace becomes available/changes
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      if (!hasWorkspace) {
        if (alive) setCampaigns([]);
        loadedRef.current = true;
        return;
      }
      try {
        const disk = await loadJSON(FILE_NAME, []);
        const list = coerceCampaignList(disk).map(normalizeCampaign);
        // Avoid overwriting user changes made while the async load was in-flight
        if (alive && !userMutatedRef.current) {
          setCampaigns(list);
        }
      } catch {
        notifyMarketing({
          id: "marketing-load-failed",
          message: "Failed to load marketing data",
          level: "error",
        });
        if (alive && !userMutatedRef.current) {
          setCampaigns([]);
        }
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace, loadJSON, notifyMarketing]);

  // ----- rehydrate after Receive (files or vault)
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      try {
        const disk = await loadJSON(FILE_NAME, []);
        setCampaigns(coerceCampaignList(disk).map(normalizeCampaign));
      } catch {
        notifyMarketing({
          id: "marketing-rehydrate-failed",
          message: "Failed to rehydrate marketing after restore",
          level: "error",
        });
      }
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setCampaigns([]);
      loadedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [loadJSON, hasWorkspace, notifyMarketing]);

  // ----- persist whenever campaigns change (debounced, after initial load)
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await saveJSON(FILE_NAME, { campaigns });
      } catch {
        notifyMarketing({
          id: "marketing-save-failed",
          message: "Failed to save marketing data",
          level: "error",
        });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [campaigns, hasWorkspace, saveJSON, notifyMarketing]);

  // Derived total
  const monthlyTotal = useMemo(
    () => campaigns.reduce((sum, c) => sum + Number(c?.cost || 0), 0),
    [campaigns]
  );

  // CRUD
  const addCampaign = useCallback((campaign) => {
    userMutatedRef.current = true;
    const c = normalizeCampaign(campaign);
    setCampaigns((prev) => [...prev, c]);
  }, []);

  const updateCampaign = useCallback((id, patch) => {
    userMutatedRef.current = true;
    setCampaigns((prev) =>
      prev.map((c) =>
        String(c.id) === String(id)
          ? normalizeCampaign({ ...c, ...patch, id: c.id })
          : c
      )
    );
  }, []);

  const removeCampaign = useCallback((id) => {
    userMutatedRef.current = true;
    setCampaigns((prev) => prev.filter((c) => String(c.id) !== String(id)));
  }, []);

  const clearAll = useCallback(() => {
    userMutatedRef.current = true;
    setCampaigns([]);
  }, []);

  const value = useMemo(
    () => ({
      campaigns,
      monthlyTotal,
      addCampaign,
      updateCampaign,
      removeCampaign,
      clearAll,
    }),
    [campaigns, monthlyTotal, addCampaign, updateCampaign, removeCampaign, clearAll]
  );

  return <MarketingContext.Provider value={value}>{children}</MarketingContext.Provider>;
}

export function useMarketing() {
  return useContext(MarketingContext);
}
